import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useFeatureFlag } from '@xemplo/feature-flag';
import { GpFeature } from '@xemplo/gp-constants';
import { PayrollDynamicRoutes, PayrollRoutes } from '@xemplo/gp-routes';
import { PayrollProcessingSystemId } from '@xemplo/gp-types';
import { useGetPayrunByIdQuery } from '@xemplo/payrun-query';
import {
  ToastHorizontalPosition,
  ToastType,
  ToastVerticalPosition,
  useToast,
} from '@xemplo/toast';

/**
 * Loads the pay run named by the route and keeps it on the route that services
 * its payroll processing system. A XemploPay pay run is inaccessible while the
 * flag is off (treated as an invalid pay run); when the flag is on, each system
 * opens in its own detail route. `system` is the system this mount serves — the
 * XemploPay route passes it so a mismatched pay run redirects to the right place.
 */
export function useValidPayrun(
  system: PayrollProcessingSystemId = PayrollProcessingSystemId.KeyPay,
) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isEnabled } = useFeatureFlag();
  const { isLoading, isError, data } = useGetPayrunByIdQuery({ id: id ?? null });

  const xemploPayEnabled = isEnabled(GpFeature.XemploPay);
  const payrunSystem = data?.result.payrollProcessingSystemId;

  const rejectAsInvalid = useCallback(() => {
    addToast({
      position: {
        vertical: ToastVerticalPosition.Top,
        horizontal: ToastHorizontalPosition.Right,
      },
      duration: 5000,
      text: 'The requested payrun does not exist or you do not have access to it',
      type: ToastType.Warning,
    });
    navigate(`/${PayrollRoutes.Payrun}`);
  }, [addToast, navigate]);

  useEffect(() => {
    if (isError) rejectAsInvalid();
  }, [isError, rejectAsInvalid]);

  useEffect(() => {
    if (isLoading || isError || !id || payrunSystem == null) return;

    // XemploPay is invisible while the flag is off — a link here is stale, so the
    // pay run is treated as inaccessible (same UX as an unknown pay run).
    if (payrunSystem === PayrollProcessingSystemId.XemploPay && !xemploPayEnabled) {
      rejectAsInvalid();
      return;
    }

    // Not the system this mount serves — send it to the route that does.
    if (payrunSystem !== system) {
      const path =
        payrunSystem === PayrollProcessingSystemId.XemploPay
          ? PayrollDynamicRoutes.PayrunXemploPayById(id)
          : PayrollDynamicRoutes.PayrunById(id);
      navigate(`/${path}`);
    }
  }, [
    isLoading,
    isError,
    id,
    payrunSystem,
    system,
    xemploPayEnabled,
    navigate,
    rejectAsInvalid,
  ]);

  // Hold the loader while a guard/redirect is pending so the wrong layout never
  // flashes before the effect runs.
  const isServicedHere =
    payrunSystem === system &&
    (payrunSystem !== PayrollProcessingSystemId.XemploPay || xemploPayEnabled);

  return { showLoader: isLoading || isError || !isServicedHere, data };
}
